import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import axios from "axios";

import s from "./Operations.module.scss";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { durationOptions } from "./components/MonitoringComponents/Constants";
import OperationsService from "../../services/operationsService";
import { toast } from "react-toastify";

const sortMaintenanceEvents = (events = []) =>
  [...events].sort((a, b) => {
    const resolveTimestamp = (event) => {
      const startSource = event.eventStart || event.event_start;
      if (startSource) {
        const startMoment = moment(startSource);
        if (startMoment.isValid()) {
          return startMoment.valueOf();
        }
      }

      const endSource = event.eventEnd || event.event_end;
      if (endSource) {
        const endMoment = moment(endSource);
        if (endMoment.isValid()) {
          return endMoment.valueOf();
        }
      }

      const createdSource = event.createdAt || event.created_at;
      if (createdSource) {
        const createdMoment = moment(createdSource);
        if (createdMoment.isValid()) {
          return createdMoment.valueOf();
        }
      }

      return Number.MAX_SAFE_INTEGER;
    };

    const diff = resolveTimestamp(a) - resolveTimestamp(b);
    if (diff !== 0) {
      return diff;
    }

    return (a.id || 0) - (b.id || 0);
  });

const toneClassName = {
  success: s.toneSuccess,
  info: s.toneInfo,
  warning: s.toneWarning,
  danger: s.toneDanger,
  neutral: s.toneNeutral,
};

const conditionClassName = {
  GOOD: s.conditionGood,
  FAIR: s.conditionFair,
  POOR: s.conditionPoor,
  BAD: s.conditionBad,
};

const eventTypeClassName = {
  maintenance: s.eventTypeMaintenance,
  optimization: s.eventTypeOptimization,
  software_update: s.eventTypeSoftwareUpdate,
  configuration: s.eventTypeConfiguration,
  emergency_drill: s.eventTypeEmergencyDrill,
  monitoring_setup: s.eventTypeMonitoringSetup,
  backup_testing: s.eventTypeBackupTesting,
};

const createEmptyMaintenanceEventForm = () => ({
  eventType: "maintenance",
  condition: "GOOD",
  assignedTo: "",
  eventStart: moment().format("YYYY-MM-DDTHH:mm"),
  eventEnd: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
  notes: "",
});

const MaintenanceStatusBadge = ({ condition }) => {
  const className = conditionClassName[condition] || conditionClassName.BAD;
  
  return (
    <span className={classNames(s.conditionBadge, className)}>
      {condition}
    </span>
  );
};

const EventTypeBadge = ({ eventType }) => {
  const className = eventTypeClassName[eventType] || eventTypeClassName.maintenance;
  const displayName = eventType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return (
    <span className={classNames(s.eventTypeBadge, className)}>
      {displayName}
    </span>
  );
};

const PowerSummaryCard = ({ substation, onToggleCanFlag, onUpdateChargeRate }) => {
  if (!substation) return null;

  const totalBuildingPower = substation.msbReadings ? 
    substation.msbReadings.reduce((sum, reading) => sum + reading, 0) : 0;
  
  const bessMsbReading = substation.bessMsbReading || 0;

  // Format power values with 2 decimal places
  const formatPower = (value) => {
    return typeof value === 'number' ? value.toFixed(2) : '0.00';
  };

  return (
    <div className={s.powerSummaryCard}>
      <div className={s.powerSummarySection}>
        <div className={s.powerSummaryHeader}>
          <h3 className={s.powerSummaryTitle}>Power Summary</h3>
        </div>
        <div className={s.powerSummaryContent}>
          <div className={s.powerSummaryItem}>
            <div className={s.powerSummaryLabel}>TOTAL BUILDING POWER</div>
            <div className={s.powerSummaryValue}>{formatPower(totalBuildingPower)} kW</div>
          </div>
          {substation.hasBess && (
            <div className={s.powerSummaryItem}>
              <div className={s.powerSummaryLabel}>BESS MSB READING</div>
              <div className={s.powerSummaryValue}>{formatPower(bessMsbReading)} kW</div>
            </div>
          )}
        </div>
      </div>

      {substation.socs && substation.socs.length > 0 && (
        <div className={s.bessSocSection}>
          <div className={s.bessSocHeader}>
             <h3 className={s.bessSocTitle}>BESS SOC</h3>
          </div>
          <div className={s.bessSocContent}>
            {substation.socs.map((bess, idx) => {
              const batteryEntry = (substation.batteryTableEntries || []).find(e => e.thing_name === bess.thing_name);
              return (
              <div>
                <div key={idx} className={s.bessSocItem}>
                    <SocMeter percent={Math.round(bess.soc * 10) / 10} />
                    <div className={s.bessSocInfo}>
                      <div className={s.bessSocName}>{bess.thing_name || `BESS ${idx + 1}`}</div>
                      <div className={s.bessSocLabel}>State of Charge</div>
                    </div>
                </div>
                {batteryEntry && (
                  <div>
                    <div className={s.bessCanFlags}>
                      <label className={s.scriptStateToggle} title="IL Auto Battery Dispatch">
                        <span className={s.scriptStateLabel}>IL Auto Battery Dispatch</span>
                        <input className={classNames(s.customCheckbox)}
                          type="checkbox"
                          checked={!!batteryEntry.can_il}
                          onChange={(e) => onToggleCanFlag(batteryEntry, 'can_il', e.target.checked)}
                        />
                        <span className={s.checkmark}></span>
                      </label>
                    </div>
                    <div className={s.bessCanFlags}>
                      <label className={s.scriptStateToggle} title="DR Auto Battery Dispatch">
                        <span className={s.scriptStateLabel}>DR Auto Battery Dispatch</span>
                        <input className={classNames(s.customCheckbox)}
                          type="checkbox"
                          checked={!!batteryEntry.can_dr}
                          onChange={(e) => onToggleCanFlag(batteryEntry, 'can_dr', e.target.checked)}
                        />
                        <span className={s.checkmark}></span>
                      </label>
                    </div>
                    <div className={s.bessCanFlags}>
                      <label className={s.scriptStateToggle} title="Unscheduled DR Charge Rate">
                        <span className={s.scriptStateLabel}>Unscheduled DR Charge Rate</span>
                        <input
                          key={`unsched-${batteryEntry.id}-${batteryEntry.unscheduled_dr_charge_rate}`}
                          type="text"
                          inputMode="decimal"
                          pattern="^[0-9]+(\.[0-9]+)?$"
                          defaultValue={batteryEntry.unscheduled_dr_charge_rate ?? ''}
                          onBlur={(e) => {
                            const newVal = e.target.value;
                            const original = batteryEntry.unscheduled_dr_charge_rate ?? '';
                            if (String(newVal) === String(original)) return;
                            if (!window.confirm(`Update Unscheduled DR Charge Rate for ${batteryEntry.thing_name} from ${original} to ${newVal}?`)) {
                              e.target.value = String(original);
                              return;
                            }
                            onUpdateChargeRate && onUpdateChargeRate(batteryEntry, newVal);
                          }}
                          style={{ width: '120px', marginLeft: '8px' }}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const MaintenanceScheduleTable = ({ maintenanceSchedule, onSaveEvent, onDeleteEvent }) => {
  const [showForm, setShowForm] = useState(false);
  const [formEvent, setFormEvent] = useState(createEmptyMaintenanceEventForm);
  const [editingEvent, setEditingEvent] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);

  const isEditing = Boolean(editingEvent);

  const eventTypes = [
    { value: "maintenance", label: "Maintenance" },
    { value: "optimization", label: "Optimization" },
    { value: "software_update", label: "Software Update" },
    { value: "configuration", label: "Configuration" },
    { value: "emergency_drill", label: "Emergency Drill" },
    { value: "monitoring_setup", label: "Monitoring Setup" },
    { value: "backup_testing", label: "Backup Testing" },
    { value: "site_visit", label: "Site Visit" },
  ];

  const conditions = [
    { value: "GOOD", label: "Good" },
    { value: "FAIR", label: "Fair" },
    { value: "POOR", label: "Poor" },
    { value: "BAD", label: "Bad" },
  ];

  const openCreateForm = () => {
    setFormEvent(createEmptyMaintenanceEventForm());
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (event) => {
    setFormEvent({
      eventType: event.eventType,
      condition: event.condition,
      assignedTo: event.assignedTo,
      eventStart: event.eventStart
        ? moment(event.eventStart).format("YYYY-MM-DDTHH:mm")
        : moment().format("YYYY-MM-DDTHH:mm"),
      eventEnd: event.eventEnd
        ? moment(event.eventEnd).format("YYYY-MM-DDTHH:mm")
        : "",
      notes: event.notes || "",
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!onSaveEvent) {
      return;
    }

    if (!formEvent.assignedTo.trim() || !formEvent.notes.trim()) {
      toast.error("Assigned to and notes are required");
      return;
    }

    if (!formEvent.eventStart) {
      toast.error("Start time is required");
      return;
    }

    const startMoment = moment(formEvent.eventStart);
    if (!startMoment.isValid()) {
      toast.error("Start time is invalid");
      return;
    }

    if (formEvent.eventEnd) {
      const endMoment = moment(formEvent.eventEnd);
      if (!endMoment.isValid()) {
        toast.error("End time is invalid");
        return;
      }
      if (endMoment.isBefore(startMoment)) {
        toast.error("End time must be after start time");
        return;
      }
    } else if (formEvent.eventType === "site_visit") {
      toast.error("End time is required for site visits");
      return;
    }

    try {
      setSavingEvent(true);
      await onSaveEvent(formEvent, editingEvent ? editingEvent.id : null);
      setFormEvent(createEmptyMaintenanceEventForm());
      setEditingEvent(null);
      setShowForm(false);
    } catch (error) {
      console.error("Failed to save maintenance event", error);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormEvent(createEmptyMaintenanceEventForm());
  };

  const handleDelete = async (event) => {
    if (!onDeleteEvent) {
      return;
    }

    if (!window.confirm("Delete this maintenance event?")) {
      return;
    }

    try {
      setDeletingEventId(event.id);
      await onDeleteEvent(event.id);
    } catch (error) {
      console.error("Failed to delete maintenance event", error);
    } finally {
      setDeletingEventId(null);
    }
  };

  const formatDateTime = (value) =>
    value ? moment(value).format("DD MMM YYYY HH:mm") : "-";

  const renderForm = () => (
          <div className={s.addEventForm}>
      <h4 className={s.addEventFormTitle}>
        {isEditing ? "Edit Event" : "Add New Event"}
      </h4>
            <div className={s.addEventFormGrid}>
              <div className={s.formField}>
                <label>Event Type</label>
                <select
            value={formEvent.eventType}
            onChange={(e) =>
              setFormEvent({ ...formEvent, eventType: e.target.value })
            }
          >
            {eventTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
                  ))}
                </select>
              </div>
              <div className={s.formField}>
                <label>Condition</label>
                <select
            value={formEvent.condition}
            onChange={(e) =>
              setFormEvent({ ...formEvent, condition: e.target.value })
            }
          >
            {conditions.map((condition) => (
              <option key={condition.value} value={condition.value}>
                {condition.label}
              </option>
                  ))}
                </select>
              </div>
              <div className={s.formField}>
                <label>Assigned To</label>
                <input
                  type="text"
            value={formEvent.assignedTo}
            onChange={(e) =>
              setFormEvent({ ...formEvent, assignedTo: e.target.value })
            }
                  placeholder="Enter assignee name"
                />
              </div>
              <div className={s.formField}>
          <label>Start Time</label>
                <input
            type="datetime-local"
            value={formEvent.eventStart}
            onChange={(e) =>
              setFormEvent({ ...formEvent, eventStart: e.target.value })
            }
            required
          />
        </div>
        <div className={s.formField}>
          <label>End Time</label>
          <input
            type="datetime-local"
            value={formEvent.eventEnd}
            onChange={(e) =>
              setFormEvent({ ...formEvent, eventEnd: e.target.value })
            }
                />
              </div>
              <div className={s.formFieldFull}>
                <label>Notes</label>
                <textarea
            value={formEvent.notes}
            onChange={(e) =>
              setFormEvent({ ...formEvent, notes: e.target.value })
            }
                  placeholder="Enter event details and notes"
                  rows={3}
                />
              </div>
            </div>
            <div className={s.addEventFormActions}>
        <button
          type="button"
          className={s.cancelButton}
          onClick={handleCancel}
          disabled={savingEvent}
        >
                Cancel
              </button>
        <button
          type="button"
          className={s.addButton}
          onClick={handleSubmit}
          disabled={savingEvent}
        >
          {isEditing ? "Update Event" : "Add Event"}
              </button>
            </div>
      </div>
    );

  const hasEvents = maintenanceSchedule && maintenanceSchedule.length > 0;

  if (!hasEvents) {
  return (
    <div className={s.maintenanceScheduleCard}>
      <div className={s.maintenanceScheduleHeader}>
          <h3 className={s.maintenanceScheduleTitle}>
            Scheduled Events & Maintenance
          </h3>
        <button 
          type="button" 
          className={s.addEventButton}
            onClick={openCreateForm}
        >
          <i className="eva eva-plus-outline"></i>
          Add Event
        </button>
      </div>
        {showForm ? (
          renderForm()
        ) : (
          <div className={s.maintenanceScheduleEmpty}>No events scheduled</div>
        )}
            </div>
    );
  }

  return (
    <div className={s.maintenanceScheduleCard}>
      <div className={s.maintenanceScheduleHeader}>
        <h3 className={s.maintenanceScheduleTitle}>
          Scheduled Events & Maintenance
        </h3>
        <button
          type="button"
          className={s.addEventButton}
          onClick={openCreateForm}
        >
          <i className="eva eva-plus-outline"></i>
          {isEditing ? "Add Another" : "Add Event"}
            </button>
          </div>
      {showForm && renderForm()}
      <div className={s.maintenanceTableWrapper}>
        <table className={s.maintenanceTable}>
          <thead>
            <tr>
              <th>Event Type</th>
              <th>Condition</th>
              <th>Assigned To</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {maintenanceSchedule.map((maintenance) => (
              <tr key={maintenance.id}>
                <td>
                  <EventTypeBadge eventType={maintenance.eventType} />
                </td>
                <td>
                  <MaintenanceStatusBadge condition={maintenance.condition} />
                </td>
                <td>{maintenance.assignedTo}</td>
                <td>{formatDateTime(maintenance.eventStart)}</td>
                <td>{formatDateTime(maintenance.eventEnd)}</td>
                <td className={s.notesCell}>{maintenance.notes}</td>
                <td>
                  <div className={s.tableActionButtons}>
                    <button
                      type="button"
                className={s.tableActionButton}
                      onClick={() => handleEditEvent(maintenance)}
                      disabled={savingEvent || deletingEventId === maintenance.id}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={classNames(
                        s.tableActionButton,
                        s.tableActionButtonDanger
                      )}
                      onClick={() => handleDelete(maintenance)}
                      disabled={deletingEventId === maintenance.id || savingEvent}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const PowerUsageModal = ({ substation, isOpen, onClose, onSaveEvent, onDeleteEvent, onToggleCanFlag, onUpdateChargeRate }) => {
  const [chartConfig, setChartConfig] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const latestSiteVisit = React.useMemo(() => {
    if (!substation?.maintenanceSchedule) {
      return null;
    }

    const siteVisits = substation.maintenanceSchedule.filter((event) => {
      const type = (event.eventType || event.event_type || "").toLowerCase();
      return type === "site_visit";
    });

    if (!siteVisits.length) {
      return null;
    }

    const endMoments = siteVisits
      .map((event) => event.eventEnd || event.event_end || event.eventStart || event.event_start)
      .filter(Boolean)
      .map((ts) => moment(ts))
      .filter((m) => m.isValid());

    if (!endMoments.length) {
      return null;
    }

    return moment.max(endMoments);
  }, [substation]);

  const fetchPowerHistory = React.useCallback(async () => {
    if (!substation || !substation.id) return;

    try {
      setChartLoading(true);
      const historyData = await OperationsService.getBuildingPowerHistory(substation.id, 6);
      
      setChartConfig({
        categories: historyData.categories,
        data: historyData.data,
      });
    } catch (err) {
      console.error("Error fetching power history:", err);
      toast.error("Failed to load power history");
      setChartConfig(null);
    } finally {
      setChartLoading(false);
    }
  }, [substation]);

  // Fetch power history when substation changes
  useEffect(() => {
    if (substation && substation.id && isOpen) {
      fetchPowerHistory();
    }
  }, [substation, isOpen, fetchPowerHistory]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "operations-power-modal",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        width: 3,
        curve: "smooth",
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.8,
          opacityFrom: 0.5,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      colors: ["#4d53e0"],
      dataLabels: { enabled: false },
      xaxis: {
        type: "category",
        categories: chartConfig ? chartConfig.categories : [],
        tickAmount: 12,
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          trim: true,
          style: {
            colors: "#6b859e",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: (value) => Math.round(value) + " kW",
          style: {
            colors: "#6b859e",
            fontSize: "12px",
          },
        },
      },
      grid: {
        strokeDashArray: 4,
      },
      tooltip: {
        y: {
          formatter: (value) => value + " kW",
        },
      },
    }),
    [chartConfig]
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className={s.powerModal}>
      <ModalHeader toggle={onClose} className={s.powerModalHeader}>
        <div className={s.modalHeaderContent}>
          <span>{substation ? substation.label + " – Details" : "Substation Details"}</span>
          <div className={s.siteVisitInfo}>
            <span className={s.siteVisitLabel}>Last Site Visit</span>
            <span className={s.siteVisitValue}>
              {latestSiteVisit
                ? latestSiteVisit.local().format("DD MMM YYYY, HH:mm")
                : "No site visit recorded"}
            </span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={s.modalContent}>
          {/* Power Summary Section */}
          <PowerSummaryCard substation={substation} onToggleCanFlag={onToggleCanFlag} onUpdateChargeRate={onUpdateChargeRate} />
          
          {/* Power Chart Section */}
          <div className={s.chartSection}>
            <h3 className={s.chartSectionTitle}>Power Usage (Past 6 Hours)</h3>
            {chartLoading ? (
              <div className={s.chartLoadingContainer}>
                <div className={s.spinner}></div>
                <p>Loading power history...</p>
              </div>
            ) : chartConfig ? (
              <ReactApexChart
                type="area"
                height={320}
                options={{
                  ...chartOptions,
                  xaxis: { ...chartOptions.xaxis, categories: chartConfig.categories },
                }}
                series={[
                  {
                    name: "Power",
                    data: chartConfig.data,
                  },
                ]}
              />
            ) : (
              <div className={s.modalPlaceholder}>Select a substation to view power usage.</div>
            )}
          </div>
          
          {/* Maintenance Schedule Section */}
          <MaintenanceScheduleTable 
            maintenanceSchedule={substation?.maintenanceSchedule} 
            onSaveEvent={onSaveEvent}
            onDeleteEvent={onDeleteEvent}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};

const TriggerTabs = []; // Kept for backwards compatibility if referenced

const TriggerModal = ({ substation, isOpen, onClose }) => {
  const [universalTime, setUniversalTime] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [universalDuration, setUniversalDuration] = useState(20);
  const [bessRates, setBessRates] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [chargeQueue, setChargeQueue] = useState([]);

  const fetchChargeQueue = React.useCallback(async () => {
    if (!substation || !substation.id) return;
    try {
      setQueueLoading(true);
      let combinedQueue = [];
      if (substation.batteryTableEntries) {
        for (const bat of substation.batteryTableEntries) {
          const queue = await OperationsService.getBuildingChargeQueue(substation.id, bat.thing_name);
          if (queue && queue.length) combinedQueue = combinedQueue.concat(queue);
        }
      }
      combinedQueue.sort((a, b) => new Date(b.desiredTime) - new Date(a.desiredTime));
      setChargeQueue(combinedQueue);
    } catch (err) {
      console.error("Error fetching charge queue:", err);
      toast.error("Failed to load recent commands");
      setChargeQueue([]);
    } finally {
      setQueueLoading(false);
    }
  }, [substation]);

  useEffect(() => {
    if (isOpen) {
      setUniversalTime(moment().format("YYYY-MM-DDTHH:mm"));
      
      const initialRates = {};
      if (substation?.batteryTableEntries) {
        substation.batteryTableEntries.forEach(bat => {
          initialRates[bat.thing_name] = "";
        });
      }
      setBessRates(initialRates);

      fetchChargeQueue();
    }
  }, [isOpen, substation, fetchChargeQueue]);

  const handleRateChange = (thingName, value) => {
    setBessRates(prev => ({
      ...prev,
      [thingName]: value
    }));
  };

  const getComputedAction = (rateStr) => {
    if (!rateStr || rateStr.trim() === "") return { action: "None", class: "" };
    const rate = parseFloat(rateStr);
    if (isNaN(rate)) return { action: "Invalid", class: "" };
    if (rate > 0) return { action: "Discharge", class: s.badgeDischarge, icon: "eva-arrow-upward-outline" };
    if (rate < 0) return { action: "Charge", class: s.badgeCharge, icon: "eva-arrow-downward-outline" };
    return { action: "Idle", class: s.badgeIdle, icon: "eva-radio-button-on-outline" };
  };

  const handleTriggerAll = async () => {
    if (!substation || !substation.id) {
      toast.error("No building selected");
      return;
    }

    try {
      setActionLoading(true);

      const entries = Object.entries(bessRates);
      if (entries.length === 0) {
        toast.error("No BESS units found");
        return;
      }

      for (const [thingName, rateStr] of entries) {
        if (!rateStr || rateStr.trim() === "") continue;
        
        const rate = parseFloat(rateStr);
        if (isNaN(rate)) continue;
        const rateInKw = Math.round(rate * 100);

        if (rate > 0) {
          await OperationsService.triggerDischarge(substation.id, {
            startDischargeDateTime: universalTime,
            dischargeDuration: universalDuration,
            dischargeRate: rateInKw,
            targetThingName: thingName,
          });
        } else if (rate < 0) {
          await OperationsService.triggerCharge(substation.id, {
            startChargeDateTime: universalTime,
            chargeDuration: universalDuration,
            chargeRate: rateInKw,
            targetThingName: thingName,
          });
        } else {
          await OperationsService.triggerIdle(substation.id, {
            startIdleDateTime: universalTime,
            targetThingName: thingName,
          });
        }
      }

      toast.success("Commands sent successfully");
      fetchChargeQueue();
    } catch (err) {
      console.error("Error triggering commands:", err);
      toast.error("Failed to trigger one or more commands");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className={s.triggerModal}>
      <div className={s.triggerModalWrapper}>
        <ModalHeader toggle={onClose} className={s.triggerMainHeader}>
          {substation ? `${substation.label} Controls` : "SICC Controls"}
        </ModalHeader>
        <ModalBody className={s.triggerModalBody}>
          <div className={s.triggerHeader}>
            <div className={s.triggerLegend}>
              <div className={s.triggerLegendItem}><strong className={s.textBlue}>+</strong> = Discharge</div>
              <div className={s.triggerLegendItem}><strong className={s.textBlue}>-</strong> = Charge</div>
              <div className={s.triggerLegendItem}><strong className={s.textBlue}>0</strong> = Idle</div>
            </div>
          </div>

          <div className={s.universalSettingsRow}>
            <div className={s.universalTimeSection}>
              <label>Universal Desired Time</label>
              <div className={s.universalTimeInputWrapper}>
                <input
                  type="datetime-local"
                  value={universalTime}
                  onChange={(e) => setUniversalTime(e.target.value)}
                  className={s.universalTimeInput}
                />
              </div>
              <small className={s.universalTimeHelper}>Applies to all BESS commands in this trigger.</small>
            </div>
            
            <div className={s.universalDurationSection}>
              <label>Control Duration</label>
              <div className={s.universalDurationInputWrapper}>
                <select
                  value={universalDuration}
                  onChange={(e) => setUniversalDuration(parseInt(e.target.value))}
                  className={s.universalDurationInput}
                >
                  {durationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} minutes
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={s.bessListWrapper}>
            <div className={s.bessListHeader}>
              <div className={s.bessListCol}>BESS NAME</div>
              <div className={classNames(s.bessListCol, s.bessListColCenter)}>DESIRED RATE (x100 kW)</div>
              <div className={classNames(s.bessListCol, s.bessListColRight)}>COMPUTED ACTION / STATE</div>
            </div>

            <div className={s.bessList}>
              {substation?.batteryTableEntries?.map((bat) => {
                const currentRate = bessRates[bat.thing_name] !== undefined ? bessRates[bat.thing_name] : "";
                const computed = getComputedAction(currentRate);
                
                return (
                  <div key={bat.thing_name} className={s.bessRow}>
                    <div className={s.bessName}>
                      <strong>{bat.thing_name}</strong>
                    </div>
                    <div className={s.bessRate}>
                      <div className={s.bessRateInputWrapper}>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="0"
                          value={currentRate}
                          onChange={(e) => handleRateChange(bat.thing_name, e.target.value)}
                        />
                        <span className={s.bessRateUnit}>x100 kW</span>
                      </div>
                    </div>
                    <div className={s.bessAction}>
                      {computed.action !== "None" && computed.action !== "Invalid" && (
                        <div className={s.bessActionCol}>
                          <div className={classNames(s.bessActionBadge, computed.class)}>
                            <i className={classNames("eva", computed.icon)}></i>
                            {computed.action}
                          </div>
                          <small className={s.bessActionHelper}>
                            {computed.action === "Discharge" ? "Rate > 0" : computed.action === "Charge" ? "Rate < 0" : "Rate = 0"}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={s.triggerActionSection}>
            <button
              type="button"
              className={classNames(s.actionButton, s.triggerSubmitBtn)}
              onClick={handleTriggerAll}
              disabled={actionLoading}
            >
              <i className="eva eva-flash"></i>
              {actionLoading ? "SENDING..." : "TRIGGER COMMAND"}
            </button>
            <div className={s.triggerFooterHelper}>
              <i className="eva eva-checkmark-circle-2-outline"></i>
              This will send the desired commands to the BESS units at the specified time.
            </div>
          </div>

          <div className={s.recentCommandsSection}>
            <h4 className={s.recentCommandsTitle}>
              <i className="eva eva-clock-outline"></i> Recent Commands
            </h4>
            <div className={s.queueTableWrapper}>
              <table className={s.queueTable}>
                <thead>
                  <tr>
                    <th>TIME TRIGGERED</th>
                    <th>BESS NAME</th>
                    <th>DESIRED RATE (x100 kW)</th>
                    <th>ACTION / STATE</th>
                    <th>TRIGGERED BY</th>
                  </tr>
                </thead>
                <tbody>
                  {queueLoading ? (
                    <tr>
                      <td colSpan={5} className={s.tableCenterMsg}>Loading queue...</td>
                    </tr>
                  ) : chargeQueue.length > 0 ? (
                    chargeQueue.map((item, idx) => (
                      <tr key={idx}>
                        <td>{moment(item.desiredTime).format('DD/MM/YYYY HH:mm')}</td>
                        <td>{item.batteryName}</td>
                        <td>{item.desiredRate !== undefined && item.desiredRate !== null && item.desiredState !== 'idle' ? Math.abs(item.desiredRate) : '-'}</td>
                        <td>
                          <span className={classNames(s.tableBadge, item.desiredState === 'discharge' ? s.badgeDischarge : item.desiredState === 'charge' ? s.badgeCharge : s.badgeIdle)}>
                            {item.desiredState.charAt(0).toUpperCase() + item.desiredState.slice(1)}
                          </span>
                        </td>
                        <td>{item.actionBy}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className={s.tableCenterMsg}>No Recent Commands</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className={s.viewFullHistoryWrapper}>
              <button className={s.viewFullHistoryBtn}>
                VIEW FULL HISTORY <i className="eva eva-external-link-outline"></i>
              </button>
            </div>
          </div>
        </ModalBody>
      </div>
    </Modal>
  );
};

const BulkTriggerModal = ({ isOpen, onClose, selectedSubstations, onSubmit }) => {
  const [universalTime, setUniversalTime] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [dischargeDuration, setDischargeDuration] = useState(35);

  return (
    <Modal isOpen={isOpen} toggle={onClose} className={s.bulkTriggerModal} centered>
      <ModalHeader toggle={onClose} className={s.bulkTriggerHeader}>
        Trigger Selected BESS
      </ModalHeader>
      <ModalBody className={s.bulkTriggerBody}>
        <div className={s.bulkTriggerField}>
          <label>Desired Time</label>
          <div className={s.dateInputWrapper}>
            <input
              type="datetime-local"
              value={universalTime}
              onChange={(e) => setUniversalTime(e.target.value)}
              className={s.dateInput}
            />
          </div>
        </div>
        <div className={s.bulkTriggerField}>
          <label>Duration (minutes)</label>
          <div className={s.dateInputWrapper}>
            <input
              type="number"
              min={1}
              value={dischargeDuration}
              onChange={(e) => setDischargeDuration(Number(e.target.value))}
              className={s.dateInput}
            />
          </div>
        </div>
        <div className={s.bulkTriggerField}>
          <label>Selected Substations</label>
          <div className={s.selectedSubstationsList}>
            <div className={s.selectedListHeader}>Substation</div>
            {selectedSubstations.map(sub => (
              <div key={sub.id} className={s.selectedListItem}>{sub.label || "—"}</div>
            ))}
          </div>
        </div>
      </ModalBody>
      <div className={s.bulkTriggerFooter}>
        <button className={classNames(s.actionButton, s.cancelButton)} onClick={onClose}>
          Cancel
        </button>
        <button className={classNames(s.actionButton, s.submitButton)} onClick={() => onSubmit(universalTime, dischargeDuration)}>
          <i className="eva eva-flash" /> Submit Trigger
        </button>
      </div>
    </Modal>
  );
};

const LoadMeter = ({ percent, tone = "neutral" }) => {
  if (typeof percent !== "number") {
    return null;
  }

  const safePercent = Math.max(0, Math.min(100, percent));

  return (
    <div className={s.loadMeter}>
      <div
        className={classNames(
          s.loadMeterFill,
          toneClassName[tone] || toneClassName.neutral
        )}
        style={{ width: safePercent + "%" }}
      />
    </div>
  );
};

const SocMeter = ({ percent }) => {
  if (typeof percent !== "number") {
    return <span className={s.actionPlaceholder}>—</span>;
  }
  
  let color = "#28a745"; // Good (75-100)
  if (percent < 20) color = "#dc3545"; // Critical
  else if (percent < 40) color = "#fd7e14"; // Low
  else if (percent < 75) color = "#ffc107"; // Moderate
  
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: '48px', height: '48px', margin: '0 auto' }}>
      <svg width="48" height="48">
        <circle cx="24" cy="24" r={radius} stroke="#f0f2f5" strokeWidth="4" fill="none" />
        <circle cx="24" cy="24" r={radius} stroke={color} strokeWidth="4" fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }} />
      </svg>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: '#16365f' }}>
        {percent}%
      </div>
    </div>
  );
};

const ActionButtons = ({ hasBess, onShowDetails, onTrigger }) => (
  <div className={s.actionsCell}>
    <button type="button" className={classNames(s.actionButton, s.actionPrimary)} onClick={onShowDetails}>
      Details
    </button>
    {hasBess ? (
      <button
        type="button"
        className={classNames(s.actionButton, s.actionSecondary, s.triggerButton)}
        onClick={onTrigger}
      >
        Trigger
      </button>
    ) : (
      <span className={s.actionPlaceholder}>No BESS</span>
    )}
  </div>
);

const SubstationRow = ({ row, onShowDetails, onTrigger }) => {
  const handleDetailsClick = () => {
    if (onShowDetails) {
      onShowDetails(row);
    }
  };

  const handleTriggerClick = () => {
    if (onTrigger) {
      onTrigger(row);
    }
  };

  const lastUpdatedMoment = row?.currentLoad?.lastUpdatedAt
    ? moment(row.currentLoad.lastUpdatedAt)
    : null;
  const lastUpdatedDisplay =
    lastUpdatedMoment && lastUpdatedMoment.isValid()
      ? lastUpdatedMoment.local().format("DD MMM YYYY HH:mm:ss")
      : "—";

  return (
    <tr className={s.zoneTableRow}>
      <td data-label="Substation" className={s.cellSubstation}>
        <div className={s.rowIdentity}>
          <i className="eva eva-home-outline" />
          <span>{row.label || "—"}</span>
        </div>
      </td>
      <td data-label="Current Load" className={s.cellCurrentLoad}>
        <span className={s.currentLoadValue}>{row.currentLoad?.valueKw ?? "—"} kW</span>
      </td>
      <td data-label="Last Updated" className={s.cellLastUpdated}>
        <span>{lastUpdatedDisplay}</span>
      </td>
      <td data-label="Average SOC (%)" className={s.cellAverageSoc} style={{ textAlign: 'center' }}>
        <SocMeter percent={row.averageSoc} />
      </td>
      <td data-label="Registered Load" className={s.cellRegisteredLoad}>
        <span className={s.registeredLoad}>{row.registeredLoad ?? "—"}</span>
      </td>
      <td data-label="Actions" className={s.cellActions}>
        <ActionButtons
          hasBess={row.hasBess}
          onShowDetails={handleDetailsClick}
          onTrigger={handleTriggerClick}
        />
      </td>
    </tr>
  );
};

const ZoneSection = ({ zone, substations, onShowDetails, onTrigger, isSelected, onSelect, onToggleScriptState }) => {
  const zoneSubstations = substations.filter(sub => 
    zone.substations.some(originalSub => originalSub.id === sub.id)
  );

  const totalLoad = zone.totalLoad?.current || 0;
  const isSelectable = zone.substations?.some(sub => sub.hasBess);
  const canScriptChangeState = !!zone.can_script_change_state;

  const handleScriptStateToggle = (e) => {
    const checked = e.target.checked;

    // Prompt for confirmation before persisting the change.
    if (!window.confirm(
      `Are you sure you want to ${checked ? "enable" : "disable"} auto trade submission for ${zone.name}?`
    )) {
      // Revert the checkbox back to its previous value.
      e.target.checked = !checked;
      return;
    }

    onToggleScriptState(zone.id, checked);
  };

  return (
    <div className={classNames(s.zoneCard, { [s.zoneCardSelected]: isSelected })}>
      <div className={s.zoneCardHeader}>
        <div className={s.zoneTitleArea}>
          <label className={classNames(s.customCheckbox, { [s.disabledCheckbox]: !isSelectable })}>
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!isSelectable}
              onChange={(e) => onSelect(zone.id, e.target.checked)}
            />
            <span className={s.checkmark}></span>
          </label>
          <span className={classNames(s.zoneTitle, { [s.disabledText]: !isSelectable })}>{zone.name}</span>
        </div>
        <div>
          <label className={s.scriptStateToggle} title="Auto Trade Submission">
            <span className={s.scriptStateLabel}>Submit Trades</span>
            <span className={classNames(s.customCheckbox, s.scriptStateCheckbox)}>
              <input
                type="checkbox"
                checked={canScriptChangeState}
                onChange={handleScriptStateToggle}
              />
              <span className={s.checkmark}></span>
            </span>
          </label>
        </div>
        <div className={s.zoneTotals}>
          Total Load: {totalLoad} kW
        </div>
      </div>
      <div className={s.zoneTableWrapper}>
        <table className={s.zoneTable}>
          <thead>
            <tr>
              <th>Substation</th>
              <th>Current Load</th>
              <th>Last Updated</th>
              <th style={{ textAlign: 'center' }}>Average SOC (%)</th>
              <th>Registered Load</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {zoneSubstations.length > 0 ? (
              zoneSubstations.map((row) => (
                <SubstationRow key={row.id} row={row} onShowDetails={onShowDetails} onTrigger={onTrigger} />
              ))
            ) : (
              <SubstationRow row={{}} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Operations = () => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeSubstation, setActiveSubstation] = useState(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerSubstation, setTriggerSubstation] = useState(null);
  const [bulkTriggerOpen, setBulkTriggerOpen] = useState(false);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [substations, setSubstations] = useState([]);
  const [zonesData, setZonesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch zones data on component mount
  useEffect(() => {
    fetchZonesData();
  }, []);

  const fetchZonesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await OperationsService.getZones();
      const normalizedZones = data.map((zone) => ({
        ...zone,
        substations: (zone.substations || []).map((substation) => ({
          ...substation,
          maintenanceSchedule: substation.maintenanceSchedule || [],
        })),
      }));
      setZonesData(normalizedZones);
      setSubstations(
        normalizedZones.flatMap((zone) => zone.substations || [])
      );
    } catch (err) {
      console.error("Error fetching zones data:", err);
      setError("Failed to load operations data. Please try again.");
      toast.error("Failed to load operations data");
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetails = async (substation) => {
    const baseSubstation = {
      ...substation,
      maintenanceSchedule: substation.maintenanceSchedule || [],
    };

    setActiveSubstation(baseSubstation);
    setDetailsOpen(true);

    try {
      const events = await OperationsService.getBuildingMaintenanceEvents(
        substation.id
      );
      const sortedEvents = sortMaintenanceEvents(events);

      setSubstations((prevSubstations) =>
        prevSubstations.map((item) =>
          item.id === substation.id
            ? { ...item, maintenanceSchedule: sortedEvents }
            : item
        )
      );

      setZonesData((prevZones) =>
        prevZones.map((zone) => ({
          ...zone,
          substations: (zone.substations || []).map((item) =>
            item.id === substation.id
              ? { ...item, maintenanceSchedule: sortedEvents }
              : item
          ),
        }))
      );

      setActiveSubstation((prev) =>
        prev && prev.id === substation.id
          ? { ...prev, maintenanceSchedule: sortedEvents }
          : prev
      );
    } catch (err) {
      console.error("Error fetching maintenance events:", err);
      toast.error("Failed to load maintenance events");
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setActiveSubstation(null);
  };

  const handleTriggerOpen = (substation) => {
    setTriggerSubstation(substation);
    setTriggerOpen(true);
  };

  const handleTriggerClose = () => {
    setTriggerOpen(false);
    setTriggerSubstation(null);
  };

  const handleSaveEvent = async (eventData, existingEventId = null) => {
    if (!activeSubstation) {
      toast.error("No substation selected");
      return;
    }

    try {
      const payload = {
        eventType: eventData.eventType,
        condition: eventData.condition,
        assignedTo: eventData.assignedTo.trim(),
        notes: eventData.notes.trim(),
        eventStart: eventData.eventStart
          ? moment(eventData.eventStart).toISOString()
          : null,
        eventEnd: eventData.eventEnd
          ? moment(eventData.eventEnd).toISOString()
          : null,
      };

      let savedEvent;
      if (existingEventId) {
        savedEvent =
          await OperationsService.updateBuildingMaintenanceEvent(
            existingEventId,
            payload
          );
      } else {
        savedEvent =
          await OperationsService.createBuildingMaintenanceEvent(
            activeSubstation.id,
            payload
          );
      }

      const buildingId = savedEvent.buildingId || activeSubstation.id;

      const updateEvents = (events = []) =>
        sortMaintenanceEvents(
          events.filter((event) => event.id !== savedEvent.id).concat(savedEvent)
        );

      setSubstations((prevSubstations) =>
        prevSubstations.map((substation) =>
          substation.id === buildingId
            ? {
                ...substation,
                maintenanceSchedule: updateEvents(
                  substation.maintenanceSchedule || []
                ),
              }
            : substation
        )
      );
      
      setZonesData((prevZones) =>
        prevZones.map((zone) => ({
          ...zone,
          substations: (zone.substations || []).map((item) =>
            item.id === buildingId
              ? {
                  ...item,
                  maintenanceSchedule: updateEvents(
                    item.maintenanceSchedule || []
                  ),
                }
              : item
          ),
        }))
      );

      setActiveSubstation((prev) =>
        prev && prev.id === buildingId
          ? {
        ...prev,
              maintenanceSchedule: updateEvents(
                prev.maintenanceSchedule || []
              ),
            }
          : prev
      );

      toast.success(
        existingEventId
          ? "Maintenance event updated"
          : "Maintenance event added"
      );
      return savedEvent;
    } catch (err) {
      console.error("Error saving maintenance event:", err);
      toast.error(
        `Failed to ${existingEventId ? "update" : "add"} maintenance event`
      );
      throw err;
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!activeSubstation) {
      toast.error("No substation selected");
      return;
    }

    const findEventById = (events = []) =>
      (events || []).find((event) => event.id === eventId);

    const existingEvent =
      findEventById(activeSubstation.maintenanceSchedule) ||
      substations
        .flatMap((substation) => substation.maintenanceSchedule || [])
        .find((event) => event.id === eventId);

    const buildingId =
      existingEvent?.buildingId || activeSubstation.id;

    try {
      await OperationsService.deleteBuildingMaintenanceEvent(eventId);

      const removeEvent = (events = []) =>
        events.filter((event) => event.id !== eventId);

      setSubstations((prevSubstations) =>
        prevSubstations.map((substation) =>
          substation.id === buildingId
            ? {
                ...substation,
                maintenanceSchedule: removeEvent(
                  substation.maintenanceSchedule || []
                ),
              }
            : substation
        )
      );

      setZonesData((prevZones) =>
        prevZones.map((zone) => ({
          ...zone,
          substations: (zone.substations || []).map((item) =>
            item.id === buildingId
              ? {
                  ...item,
                  maintenanceSchedule: removeEvent(
                    item.maintenanceSchedule || []
                  ),
                }
              : item
          ),
        }))
      );

      setActiveSubstation((prev) =>
        prev && prev.id === buildingId
          ? {
              ...prev,
              maintenanceSchedule: removeEvent(
                prev.maintenanceSchedule || []
              ),
            }
          : prev
      );

      toast.success("Maintenance event deleted");
    } catch (err) {
      console.error("Error deleting maintenance event:", err);
      toast.error("Failed to delete maintenance event");
      throw err;
    }
  };

  const selectableZones = useMemo(() => zonesData.filter(zone => zone.substations?.some(sub => sub.hasBess)), [zonesData]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFacilities(selectableZones.map(z => z.id));
    } else {
      setSelectedFacilities([]);
    }
  };

  const handleFacilitySelect = (zoneId, checked) => {
    if (checked) {
      setSelectedFacilities(prev => [...prev, zoneId]);
    } else {
      setSelectedFacilities(prev => prev.filter(id => id !== zoneId));
    }
  };

  const handleToggleScriptState = async (zoneId, checked) => {
    try {
      // Persist the change to the database.
      await OperationsService.updateZoneScriptState(zoneId, checked);

      // Optimistically reflect the new value in local state.
      setZonesData((prevZones) =>
        prevZones.map((zone) =>
          zone.id === zoneId
            ? { ...zone, can_script_change_state: checked }
            : zone
        )
      );

      toast.success("Auto trade submission setting updated");
    } catch (err) {
      console.error("Error updating auto trade submission setting:", err);
      toast.error("Failed to update auto trade submission setting");
    }
  };

  const handleToggleCanFlag = async (entry, field, checked) => {
    const action = checked ? 'enable' : 'disable';

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${field === 'can_il' ? 'IL' : 'DR'} auto battery dispatch for ${entry.thing_name}?`
      )
    ) {
      return;
    }

    try {
      await axios.put(`/table_names/${entry.id}`, {
        [field]: checked
      });

      // Update activeSubstation
      setActiveSubstation(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          batteryTableEntries: (prev.batteryTableEntries || []).map(item =>
            item.id === entry.id
              ? { ...item, [field]: checked }
              : item
          )
        };
      });

      // Also update substations state
      setSubstations(prev =>
        prev.map(substation =>
          substation.id === activeSubstation?.id
            ? {
                ...substation,
                batteryTableEntries: (substation.batteryTableEntries || []).map(
                  item =>
                    item.id === entry.id
                      ? { ...item, [field]: checked }
                      : item
                )
              }
            : substation
        )
      );

      // Also update zonesData, which is the main source for ZoneSection
      setZonesData(prevZones =>
        prevZones.map(zone => ({
          ...zone,
          substations: (zone.substations || []).map(substation =>
            substation.id === activeSubstation?.id
              ? {
                  ...substation,
                  batteryTableEntries: (
                    substation.batteryTableEntries || []
                  ).map(item =>
                    item.id === entry.id
                      ? { ...item, [field]: checked }
                      : item
                  )
                }
              : substation
          )
        }))
      );

      toast.success(
        `${field === 'can_il' ? 'IL' : 'DR'} auto battery dispatch flag updated for ${entry.thing_name}`
      );
    } catch (err) {
      console.error(
        'Error updating auto battery dispatch setting:',
        err
      );

      toast.error('Failed to update auto battery dispatch setting');
    }
  };

  const handleUpdateUnscheduledChargeRate = async (entry, value) => {
    const numericValue = value === '' ? null : Number.parseFloat(value);
    if (value !== '' && isNaN(numericValue)) {
      toast.error('Invalid decimal number');
      return;
    }
    try {
      await axios.put(`/table_names/${entry.id}`, {
        unscheduled_dr_charge_rate: numericValue
      });

      // Update activeSubstation
      setActiveSubstation(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          batteryTableEntries: (prev.batteryTableEntries || []).map(item =>
            item.id === entry.id
              ? { ...item, unscheduled_dr_charge_rate: numericValue }
              : item
          )
        };
      });

      // Also update substations state
      setSubstations(prev => prev.map(substation =>
        substation.id === activeSubstation?.id
          ? {
              ...substation,
              batteryTableEntries: (substation.batteryTableEntries || []).map(item =>
                item.id === entry.id
                  ? { ...item, unscheduled_dr_charge_rate: numericValue }
                  : item
              )
            }
          : substation
      ));

      // Also update zonesData
      setZonesData(prevZones => prevZones.map(zone => ({
        ...zone,
        substations: (zone.substations || []).map(substation =>
          substation.id === activeSubstation?.id
            ? {
                ...substation,
                batteryTableEntries: (substation.batteryTableEntries || []).map(item =>
                  item.id === entry.id
                    ? { ...item, unscheduled_dr_charge_rate: numericValue }
                    : item
                )
              }
            : substation
        )
      })));

      toast.success(`Unscheduled DR charge rate updated for ${entry.thing_name}`);
    } catch (err) {
      console.error('Error updating unscheduled DR charge rate:', err);
      toast.error('Failed to update unscheduled DR charge rate');
    }
  };

  const isAllSelected = selectableZones.length > 0 && selectedFacilities.length === selectableZones.length;
  const isPartiallySelected = selectedFacilities.length > 0 && selectedFacilities.length < selectableZones.length;
  const totalSubstations = zonesData.reduce((acc, zone) => acc + (zone.substations?.length || 0), 0);
  
  const selectedSubstationObjs = useMemo(() => {
    return zonesData
      .filter(z => selectedFacilities.includes(z.id))
      .flatMap(z => z.substations || []);
  }, [zonesData, selectedFacilities]);

  if (loading) {
    return (
      <div className={s.operationsPage}>
        <div className={s.pageIntro}>
          <span className={s.pageKicker}>Facilities</span>
          <h1 className={s.pageTitle}>Load Overview</h1>
        </div>
        <div className={s.loadingContainer}>
          <div className={s.spinner}></div>
          <p>Loading operations data...</p>
        </div>
      </div>
    );
  }

  if (error && zonesData.length === 0) {
    return (
      <div className={s.operationsPage}>
        <div className={s.pageIntro}>
          <span className={s.pageKicker}>Facilities</span>
          <h1 className={s.pageTitle}>Load Overview</h1>
        </div>
        <div className={s.errorContainer}>
          <p className={s.errorMessage}>{error}</p>
          <button onClick={fetchZonesData} className={s.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleBulkSubmit = async (startDischargeDateTime, dischargeDuration) => {
    const buildingIds = selectedSubstationObjs.map(s => s.id);
    try {
      const result = await OperationsService.triggerBulkCharge(
        buildingIds,
        startDischargeDateTime,
        dischargeDuration
      );

      const successCount = result.succeeded?.length || 0;
      const failCount = result.failed?.length || 0;

      if (failCount === 0) {
        toast.success(`Discharge command queued for ${successCount} building${successCount !== 1 ? 's' : ''} at ${moment(startDischargeDateTime).format("HH:mm")}`);
      } else {
        toast.warning(`${successCount} succeeded, ${failCount} failed. Check console for details.`);
        console.warn("Bulk discharge failures:", result.failed);
      }
    } catch (err) {
      console.error("Bulk trigger error:", err);
      toast.error("Failed to send bulk discharge command. Please try again.");
    } finally {
      setBulkTriggerOpen(false);
    }
  };

  return (
    <div className={s.operationsPage}>
      <div className={s.pageIntro}>
        <span className={s.pageKicker}>FACILITIES</span>
        <h1 className={s.pageTitle}>Load Overview</h1>
        <p className={s.pageSubtitle}>Monitor facility load and trigger BESS response.</p>
      </div>

      <div className={s.globalActionBar}>
        <div className={s.globalSelectionArea}>
          <label className={s.customCheckbox}>
            <input
              type="checkbox"
              checked={isAllSelected}
              ref={el => { if (el) el.indeterminate = isPartiallySelected; }}
              onChange={handleSelectAll}
            />
            <span className={s.checkmark}></span>
          </label>
          <span className={s.selectAllLabel}>Select all facilities</span>
          <span className={s.statsLabel}>
            <span className={s.statsNumber}>{zonesData.length}</span> facilities
          </span>
          <span className={s.statsDivider}></span>
          <span className={s.statsLabel}>
            <span className={s.statsNumber}>{totalSubstations}</span> substations
          </span>
        </div>
        <div className={s.globalActionRight}>
          <span className={s.selectedCount}>Selected: <span className={s.selectedCountNumber}>{selectedFacilities.length}</span></span>
          <button
            className={classNames(s.actionButton, s.triggerSelectedButton)}
            disabled={selectedFacilities.length === 0}
            onClick={() => setBulkTriggerOpen(true)}
          >
            <i className="eva eva-flash" /> Trigger Selected
          </button>
        </div>
      </div>

      <div className={s.zoneStack}>
        {zonesData.map((zone) => (
          <ZoneSection
            key={zone.id}
            zone={zone}
            substations={substations}
            onShowDetails={handleShowDetails}
            onTrigger={handleTriggerOpen}
            isSelected={selectedFacilities.includes(zone.id)}
            onSelect={handleFacilitySelect}
            onToggleScriptState={handleToggleScriptState}
          />
        ))}
      </div>



      <PowerUsageModal
        substation={activeSubstation}
        isOpen={detailsOpen}
        onClose={handleCloseDetails}
        onSaveEvent={handleSaveEvent}
        onDeleteEvent={handleDeleteEvent}
        onToggleCanFlag={handleToggleCanFlag}
        onUpdateChargeRate={handleUpdateUnscheduledChargeRate}
      />
      <TriggerModal substation={triggerSubstation} isOpen={triggerOpen} onClose={handleTriggerClose} />
      <BulkTriggerModal
        isOpen={bulkTriggerOpen}
        onClose={() => setBulkTriggerOpen(false)}
        selectedSubstations={selectedSubstationObjs}
        onSubmit={handleBulkSubmit}
      />
    </div>
  );
};

export default Operations;
